import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Shield, Globe, Loader2, AlertCircle, Lock, CheckCircle, Zap, Scale, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { readPendingDomain, clearPendingDomain } from '../components/DomainStart';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

const scanFeatures = [
  { label: 'SSL/TLS & Certificat',             plan: 'starter', icon: '🔒' },
  { label: 'Redirection HTTP → HTTPS',         plan: 'starter', icon: '🔄' },
  { label: 'Headers HSTS, CSP, X-Frame',       plan: 'starter', icon: '🛡️' },
  { label: 'Scan DNS (SPF, DMARC, DKIM, MX)',  plan: 'starter', icon: '🌐' },
  { label: 'X-Content-Type, Referrer-Policy',  plan: 'pro',     icon: '⚙️' },
  { label: 'Permissions-Policy',               plan: 'pro',     icon: '🎛️' },
  { label: 'Analyse CORS',                     plan: 'pro',     icon: '🔗' },
  { label: 'Sécurité des Cookies',             plan: 'pro',     icon: '🍪' },
  { label: 'Détection version serveur',        plan: 'pro',     icon: '🖥️' },
  { label: 'Rapports IA détaillés',            plan: 'pro',     icon: '🤖' },
  { label: 'Fichiers sensibles exposés *',     plan: 'pro',     icon: '🚨' },
  { label: 'Versions TLS & qualité CSP *',     plan: 'pro',     icon: '🔐' },
  { label: 'CMS obsolète & sous-domaines *',   plan: 'pro',     icon: '🗺️' },
  { label: 'PDF & partage à votre marque',     plan: 'enterprise', icon: '📄' },
];

const planLimits = { free: 0, starter: 5, pro: 30, enterprise: 999 };
const planColors = {
  free:       'text-zinc-400 border-zinc-700',
  starter:    'text-blue-400 border-blue-500/30',
  pro:        'text-green-400 border-green-500/30',
  enterprise: 'text-purple-400 border-purple-500/30',
};

const SCAN_STEPS = {
  idle:      null,
  scanning:  'Analyse en cours...',
  report:    'Génération du rapport IA...',
  done:      'Terminé !',
};

export default function NewAudit() {
  const [domain, setDomain] = useState(readPendingDomain);
  const [auditName, setAuditName] = useState('');
  const [step, setStep] = useState('idle');
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { token, user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(searchParams.get('client') || '');
  const isAgency = user?.subscription_plan === 'enterprise';

  useEffect(() => {
    if (!isAgency || !token) return;
    axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setClients(r.data || []))
      .catch(() => {});
  }, [isAgency, token]);

  const plan = user?.subscription_plan || 'free';
  const canGenerateReport = plan === 'pro' || plan === 'enterprise';
  // Plan gratuit : un audit offert par compte (score + liste des failles, sans rapport IA)
  const freeAuditAvailable = plan === 'free' && user?.free_audit_available === true;
  const mustChoosePlan = plan === 'free' && !freeAuditAvailable;
  const emailBlocked = freeAuditAvailable && user?.email_verified === false;

  const isFeatureAvailable = (featurePlan) => {
    const order = { starter: 1, pro: 2, enterprise: 3 };
    const userOrder = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    return userOrder[plan] >= order[featurePlan];
  };

  const getPlanLabel = () => {
    if (plan === 'free') return freeAuditAvailable ? 'Plan gratuit — 1 audit offert' : 'Audit gratuit déjà utilisé';
    const limit = planLimits[plan];
    return `Plan ${plan.charAt(0).toUpperCase() + plan.slice(1)} — ${limit === 999 ? 'Illimité' : `${limit} audits/mois`}`;
  };

  const handleStartScan = async (e) => {
    e.preventDefault();
    if (mustChoosePlan) { navigate('/billing'); return; }

    setStep('scanning');
    setError(null);

    const cleanDomain = domain
      .replace(/^(?:https?:\/\/)?(?:www\.)?/i, "")
      .split('/')[0].split('?')[0].trim();

    const authToken = token || localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

    try {
      // Étape 1 — Scan
      const scanRes = await axios.post(`${API_URL}/api/audits/scan`, {
        target_domain: cleanDomain,
        name: auditName.trim() || `Scan de ${cleanDomain}`,
        audit_types: ["web_vuln"],
        ...(isAgency && clientId ? { client_id: clientId } : {}),
      }, { headers, timeout: 60000 });

      const auditId = scanRes.data?.audit_id;
      clearPendingDomain();
      if (!auditId) throw new Error("Scan échoué");

      // Étape 2 — Rapport IA (si plan Pro/Enterprise)
      if (canGenerateReport) {
        setStep('report');
        try {
          const reportRes = await axios.post(
            `${API_URL}/api/audits/${auditId}/report`,
            {},
            { headers, timeout: 60000 }
          );
          const reportId = reportRes.data?.report_id;
          if (reportId) {
            setStep('done');
            setTimeout(() => navigate(`/reports/${reportId}`), 600);
            return;
          }
        } catch (reportErr) {
          console.error('Erreur rapport IA, redirection vers audit:', reportErr);
        }
      }

      // Plan gratuit, Starter ou erreur rapport → affiche les résultats bruts
      if (plan === 'free' && checkAuth) checkAuth();
      setStep('done');
      setTimeout(() => navigate(`/audits/${auditId}`), 600);

    } catch (err) {
      console.error("❌ ERREUR:", err);
      if (err.response?.status === 429) {
        setError("Trop de requêtes. Veuillez patienter avant le prochain scan.");
      } else if (err.response?.status === 403) {
        setError(`${err.response.data?.detail || "Limite atteinte"}. Passez au plan supérieur.`);
      } else if (err.response) {
        setError(`Erreur (${err.response.status}): ${err.response.data?.detail || "Erreur interne"}`);
      } else {
        setError("Le serveur ne répond pas ou le scan prend trop de temps.");
      }
      setStep('idle');
    }
  };

  const loading = step === 'scanning' || step === 'report' || step === 'done';

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">

        {/* Header */}
        <div className="text-center">
          <Shield className="w-12 h-12 text-blue-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white font-mono tracking-tighter italic uppercase">
            Initiate_Security_Audit
          </h1>
          <p className="text-zinc-500 text-xs font-mono mt-2 uppercase tracking-widest">
            Analyse SSL, HTTP Headers & DNS en temps réel
          </p>
        </div>

        {/* Audit gratuit disponible */}
        {freeAuditAvailable && (
          <div className="bg-blue-500/5 border border-blue-500/30 rounded-sm p-4">
            <p className="text-blue-300 font-mono text-xs uppercase">
              🎁 Votre premier audit est offert — score de sécurité et liste des failles de votre site, sans carte bancaire
            </p>
          </div>
        )}

        {/* Audit gratuit déjà utilisé */}
        {mustChoosePlan && (
          <div className="bg-yellow-500/5 border border-yellow-500/30 rounded-sm p-4 flex items-center justify-between">
            <p className="text-yellow-400 font-mono text-xs uppercase">
              ⚠️ Vous avez utilisé votre audit gratuit — choisissez un plan pour lancer de nouveaux audits
            </p>
            <button onClick={() => navigate('/billing')} className="text-blue-400 font-mono text-xs uppercase underline ml-4 shrink-0 hover:text-blue-300">
              Voir les plans →
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Formulaire */}
          <div className="bg-zinc-900/50 border border-zinc-800 p-8 rounded-sm space-y-6 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500 to-blue-500/0 opacity-30" />

            {/* Badge plan */}
            <div className={`inline-flex items-center gap-2 px-3 py-1 border rounded-sm text-xs font-mono uppercase ${planColors[plan]}`}>
              <Zap className="w-3 h-3" />
              {getPlanLabel()}
            </div>

            {/* Domaine */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-blue-500 font-mono tracking-[0.2em] uppercase">
                TARGET_DOMAIN_INPUT
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <input
                  type="text"
                  className="w-full bg-black border border-zinc-800 p-4 pl-10 text-white font-mono focus:border-blue-500 outline-none transition-all placeholder:text-zinc-800"
                  placeholder="domain.com"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Nom */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-zinc-500 font-mono tracking-[0.2em] uppercase">
                INTERNAL_SCAN_LABEL
              </label>
              <input
                type="text"
                className="w-full bg-black border border-zinc-800 p-4 text-white font-mono focus:border-blue-500 outline-none transition-all placeholder:text-zinc-800"
                placeholder="Ex: Main_Site_Check"
                value={auditName}
                onChange={(e) => setAuditName(e.target.value)}
                disabled={loading}
              />
            </div>

            {/* Client (plan Enterprise) */}
            {isAgency && (
              <div className="space-y-2">
                <label htmlFor="audit-client" className="block text-[10px] font-bold text-zinc-500 font-mono tracking-[0.2em] uppercase">
                  CLIENT
                </label>
                <select id="audit-client" value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={loading}
                  className="w-full bg-black border border-zinc-800 p-4 text-white font-mono focus:border-purple-500 outline-none">
                  <option value="">— Aucun client —</option>
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {/* Étapes de progression */}
            {loading && (
              <div className="space-y-2 p-4 bg-blue-500/5 border border-blue-500/20 rounded-sm">
                {step === 'scanning' && (
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                    <div>
                      <p className="text-blue-400 font-mono text-xs font-bold">Scan en cours...</p>
                      <p className="text-zinc-500 font-mono text-[10px]">SSL · DNS · Headers HTTP</p>
                    </div>
                  </div>
                )}
                {step === 'report' && (
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-4 h-4 text-purple-400 animate-pulse shrink-0" />
                    <div>
                      <p className="text-purple-400 font-mono text-xs font-bold">Génération du rapport IA...</p>
                      <p className="text-zinc-500 font-mono text-[10px]">Analyse et recommandations en cours</p>
                    </div>
                  </div>
                )}
                {step === 'done' && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                    <p className="text-green-400 font-mono text-xs font-bold">Terminé ! Redirection...</p>
                  </div>
                )}
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div className="p-4 bg-red-500/5 border-l-2 border-red-500 text-red-500 text-xs font-mono leading-relaxed">
                <div className="flex gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>[ERROR]: {error}</span>
                </div>
                {error.includes('Limite') && (
                  <button onClick={() => navigate('/billing')} className="mt-2 text-blue-400 underline text-[10px] font-mono uppercase">
                    → Voir les plans
                  </button>
                )}
              </div>
            )}

            <div className="space-y-4">
              <Button
                type="button"
                onClick={handleStartScan}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-8 rounded-none font-black text-lg font-mono transition-all active:scale-[0.98] disabled:opacity-50"
                disabled={loading || emailBlocked || (!mustChoosePlan && (!domain || !domain.includes('.')))}
              >
                {loading ? (
                  <div className="flex items-center gap-3">
                    <Loader2 className="animate-spin w-5 h-5" />
                    <span className="animate-pulse">{SCAN_STEPS[step]}</span>
                  </div>
                ) : mustChoosePlan ? 'CHOISIR UN PLAN →' : emailBlocked ? 'CONFIRMEZ VOTRE EMAIL' : freeAuditAvailable ? 'LANCER MON AUDIT GRATUIT' : 'EXECUTE_SECURITY_SCAN'}
              </Button>

              {/* Disclaimer légal */}
              <div className="flex gap-3 p-3 bg-black/40 border border-zinc-800/50 rounded-sm">
                <Scale className="w-4 h-4 text-zinc-600 shrink-0 mt-0.5" />
                <p className="text-[9px] leading-relaxed text-zinc-500 font-mono uppercase">
                  <span className="text-zinc-400 font-bold">[LEGAL_NOTICE]:</span> En lançant cet audit, vous certifiez être propriétaire de la cible ou disposer d'une autorisation explicite.
                </p>
              </div>
            </div>
          </div>

          {/* Features par plan */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 space-y-3">
            <h2 className="text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest border-b border-zinc-800 pb-2">
              Checks inclus dans votre scan
            </h2>
            {scanFeatures.map((f, i) => {
              const available = isFeatureAvailable(f.plan);
              return (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-sm transition-all ${available ? '' : 'opacity-40'}`}>
                  <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                    {available
                      ? <CheckCircle className="w-4 h-4 text-green-500" />
                      : <Lock className="w-4 h-4 text-zinc-600" />
                    }
                  </div>
                  <span className="text-xs font-mono text-zinc-300">{f.icon} {f.label}</span>
                  {!available && (
                    <span className={`ml-auto text-[9px] font-mono uppercase px-2 py-0.5 border rounded-sm shrink-0 ${
                      f.plan === 'enterprise' ? 'text-purple-400 border-purple-500/30' :
                      f.plan === 'pro' ? 'text-green-400 border-green-500/30' :
                      'text-blue-400 border-blue-500/30'
                    }`}>
                      {f.plan}
                    </span>
                  )}
                </div>
              );
            })}
            <p className="text-[10px] font-mono text-zinc-600 leading-relaxed pt-1">
              * Analyse approfondie, lancée uniquement sur un domaine vérifié dans{' '}
              <button type="button" onClick={() => navigate('/domains')} className="text-blue-400 hover:underline">Mes domaines</button>.
            </p>
            {plan === 'free' && (
              <button onClick={() => navigate('/billing')} className="w-full mt-4 py-3 border border-blue-500/30 text-blue-400 text-xs font-mono uppercase rounded-sm hover:bg-blue-500/10 transition-all">
                → Débloquer toutes les fonctionnalités
              </button>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
